import type { ProductKey } from '@/router/products'
import { SubscriptionFlowDefinition } from './SubscriptionFlowDefinition'
import { HealthExpensesFlow } from './HealthExpensesFlow'
import { AbroadFlow } from './AbroadFlow'
import { ArrivingFranceFlow } from './ArrivingFranceFlow'
import { CivilLiabilityFlow } from './CivilLiabilityFlow'
import { HealthTopUpFlow } from './HealthTopUpFlow'
import { RegionalAuraFlow } from './RegionalAuraFlow'
import { SolidariteExamFlow } from './SolidariteExamFlow'

/**
 * Registry of the wired subscription flows, one definition per product. A
 * product absent here is not yet backed by the API (it still uses the
 * front-computed pricing path in `useSubscription`).
 */
const registry: Partial<Record<ProductKey, SubscriptionFlowDefinition>> = {
  healthExpenses: new HealthExpensesFlow(),
  abroad: new AbroadFlow(),
  arrivingFrance: new ArrivingFranceFlow(),
  civil: new CivilLiabilityFlow(),
  healthTopUp: new HealthTopUpFlow(),
  regionalAura: new RegionalAuraFlow(),
  solidariteExam: new SolidariteExamFlow(),
}

/** The subscription-flow definition for a product, or null when not wired. */
export function subscriptionFlowFor(
  product: ProductKey | undefined,
): SubscriptionFlowDefinition | null {
  return product ? registry[product] ?? null : null
}

/**
 * Whether a member already covered for this product may subscribe to it again.
 *
 * Reads the answer off the flow definition instead of holding a product list of its own, so
 * a product that starts (or stops) allowing repeats is changed in one place. A product with
 * no wired flow answers false: nothing is known about it, and refusing is the safe default.
 */
export function allowsRepeatSubscription(product: ProductKey | undefined): boolean {
  return subscriptionFlowFor(product)?.allowsRepeatSubscription ?? false
}

export { SubscriptionFlowDefinition }
export type {
  FlowAnswers,
  FlowPeriod,
  FlowPeriodInput,
  FlowFieldRequirements,
  PriceTag,
  CrossSellConfig,
  TopUpConfig,
} from './SubscriptionFlowDefinition'
